import type { EstimateResponse } from '@ks-studio/shared';

/** Which of the three screens is showing. */
export type Step = 'address' | 'contact' | 'result';

/**
 * State that crosses a step boundary. In memory only: no localStorage, no
 * sessionStorage, no cookie.
 *
 * THE RULE, and it is why this is shorter than dac-qualifier's: this type holds
 * only what a LATER step reads. Anything a step needs while it is mounted and
 * never again lives in that component. Step 1's query text, its suggestion list,
 * its busy and error flags, its Places session token and the decline reason are
 * all local to `Step1Address` for that reason. The decline reason is the clearest
 * case: a declined address cannot reach step 2 by construction, so there is no
 * later step that could read it, and a field nothing can read is a field that
 * rots.
 *
 * Nothing is persisted because a partly-filled contact form left on a shared or
 * kiosk browser must not be recoverable by the next person, and the flow is three
 * screens long.
 */
export type WidgetState = {
  /**
   * Minted ONCE per widget mount and never reassigned. This is the mechanism,
   * not a log id: n8n dedupes on it and ADR-0024 makes it the Zendesk Sell
   * upsert filter, so a submission that times out and is retried becomes one CRM
   * record. Minting it server-side, or re-minting it on a retry, produces the
   * duplicate it exists to prevent.
   *
   * NOT the same thing as the Places session token, which is minted per address
   * attempt in `Step1Address` and is not held here.
   *
   * @see docs/adr/0013-browser-mints-the-submission-id.md
   */
  submissionId: string;
  step: Step;

  /**
   * The served address, as the gate resolved it. Step 3 displays it.
   *
   * `county` is the bare name the gate works in, `Albany` rather than
   * `Albany County`; `normalizeCounty` strips the suffix so Google's
   * `administrative_area_level_2` can be compared against our own map.
   */
  resolved: { formatted: string; county: string; lat: number; lng: number } | null;

  /**
   * The ONLY channel by which an address reaches `estimate`. There is no address
   * field on `EstimateRequest` to tamper with.
   *
   * @see docs/adr/0004-signed-address-token-is-the-only-channel.md
   */
  addressToken: string | null;

  /**
   * `phone` is a plain string, not `string | null` as in dac-qualifier:
   * `EstimateRequest.contact.phone` is required, so step 2 must make it a
   * required field rather than offering it as optional and sending ''.
   */
  firstName: string;
  lastName: string;
  email: string;
  phone: string;

  estimate: EstimateResponse | null;

  /**
   * A message from the step that sent the customer here, rendered by the step that
   * receives them. Null in the normal case.
   *
   * This is the ONE piece of per-step UI state that earns a place in `WidgetState`,
   * and it earns it by crossing a boundary: when `estimate` rejects with
   * `permission-denied` the address token is dead for good, so step 2 returns the
   * customer to step 1, and the reason has to travel with them or they arrive at a
   * re-set form with no idea why. Everything else a step needs stays in that step.
   */
  notice: string | null;
};

/** Fresh state for one widget mount. Mints the submission id; see the field's note. */
export function initialState(): WidgetState {
  return {
    submissionId: crypto.randomUUID(),
    step: 'address',
    resolved: null,
    addressToken: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    estimate: null,
    notice: null,
  };
}
